import {createSelector} from "@reduxjs/toolkit";
import {RootState} from "../../../store";

const selectMaintenanceState = (state: RootState) => state.maintenance;

// ── Шаблоны ───────────────────────────────────────────────────────────────

export const selectAllTemplates = createSelector(
    [selectMaintenanceState],
    (s) => s.templates,
);

export const selectTemplatesLoading = createSelector(
    [selectMaintenanceState],
    (s) => s.templatesLoading,
);

export const selectCurrentTemplate = createSelector(
    [selectMaintenanceState],
    (s) => s.currentTemplate,
);

export const selectActiveTemplates = createSelector(
    [selectAllTemplates],
    (templates) => templates.filter((t) => t.is_active),
);

// ── Дашборд ───────────────────────────────────────────────────────────────

export const selectDashboard = createSelector(
    [selectMaintenanceState],
    (s) => s.dashboard,
);

export const selectDashboardLoading = createSelector(
    [selectMaintenanceState],
    (s) => s.dashboardLoading,
);

/** Машины с просроченным ТО (remaining_km < 0 или remaining_hours < 0) */
export const selectOverdueMachinery = createSelector(
    [selectDashboard],
    (rows) =>
        rows.filter(
            (r) =>
                (r.remaining_km != null && r.remaining_km < 0) ||
                (r.remaining_hours != null && r.remaining_hours < 0),
        ),
);

/** Машины без данных о ТО (ни одного завершённого и ни одного запланированного) */
export const selectNoMaintenanceData = createSelector(
    [selectDashboard],
    (rows) =>
        rows.filter((r) => r.last_task_id == null && r.next_task_id == null),
);

export const selectActiveTemplatesForOptions = createSelector(
    [selectActiveTemplates],
    (templates) => templates.map((t) => ({id: t.id, title: t.title})),
);

export const selectMaintenanceHistory = createSelector(
    [selectMaintenanceState],
    (state) => {
        const upcoming = state.history
            .filter((t) => t.status_id !== 3)
            .sort((a, b) => +a.due_date - +b.due_date); // ближайший срок сверху
        const completed = state.history
            .filter((t) => t.status_id === 3)
            .sort((a, b) => +b.result_date - +a.result_date); // новые сверху
        return [...upcoming, ...completed];
    },
);

export const selectLastMaintenanceHistory = createSelector(
    [selectMaintenanceState],
    (state) => {
        const upcoming = state.history
            .filter((t) => t.status_id !== 3)
            .sort((a, b) => +a.due_date - +b.due_date); // ближайший срок сверху
        const completed = state.history
            .filter((t) => t.status_id === 3)
            .sort((a, b) => +b.result_date - +a.result_date); // новые сверху

        const TOTAL = 6;
        // три ближайших к границе предстоящих, по убыванию due_date (октябрь → сентябрь → август)
        const upcomingSlice = upcoming.slice(-Math.min(3, upcoming.length)).reverse();
        // добираем завершёнными до TOTAL (новые сверху: июль → июнь → май)
        const completedSlice = completed.slice(0, TOTAL - upcomingSlice.length);

        return [...upcomingSlice, ...completedSlice];
    },
);

export const selectMaintenanceHistoryLoading = createSelector(
    [selectMaintenanceState],
    (state) => state.historyLoading,
);

export const selectMaintenanceHistoryTypeFilter = createSelector(
    [selectMaintenanceState],
    (state) => state.historyTypeFilter,
);

/**
 * История ТО после фильтра по типу техники. Тип берётся из вложенного
 * task.machinery?.type_id. Строится поверх selectMaintenanceHistory —
 * сортировку upcoming/completed не дублируем. state.history НЕ трогаем:
 * прогноз ТО (selectLastMaintenanceHistory) считается по полной истории.
 */
export const selectFilteredMaintenanceHistory = createSelector(
    [selectMaintenanceHistory, selectMaintenanceHistoryTypeFilter],
    (history, typeFilter) =>
        typeFilter == null
            ? history
            : history.filter((t) => t.machinery?.type_id === typeFilter),
);